import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, CalendarPlus } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const CalendarConfirmDialog = ({ open, onOpenChange, commitment, onDone }) => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open || !commitment) return;
    setTitle(commitment.promise_text || "");
    setDate((commitment.expected_completion || "").slice(0, 10));
  }, [open, commitment]);

  const save = async () => {
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    setSaving(true);
    try {
      await api.addCalendar(commitment.id, { title, date, note: "Demo calendar event" });
      const ics = await fetch(api.icsUrl(commitment.id));
      const blob = await ics.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commitment-${commitment.id.slice(0, 8)}.ics`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Demo calendar event saved · .ics downloaded");
      onDone?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to add calendar event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="calendar-dialog"
        className="max-w-lg bg-ledger-surface border border-ledger-border p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-7 pb-4 border-b border-ledger-border">
          <DialogTitle className="font-display text-2xl text-ledger-ink flex items-center gap-2">
            <CalendarPlus className="h-5 w-5" /> Demo calendar event
          </DialogTitle>
          <DialogDescription className="text-sm text-ledger-sub">
            Saved inside Commitment Ledger. Nothing is sent to a real calendar.
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-5 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-1">Title</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="calendar-title"
              className="w-full bg-white border border-ledger-border rounded-sm px-3 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-ledger-ink"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted mb-1">Date</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="calendar-date"
              className="w-full bg-white border border-ledger-border rounded-sm px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ledger-ink"
            />
          </div>
          <div className="text-[11px] font-mono text-ledger-muted">
            Downloads a .ics you can drag into Google Calendar or Outlook if you want.
          </div>
        </div>

        <div className="px-8 py-4 border-t border-ledger-border flex items-center justify-between">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="calendar-cancel"
            className="px-4 py-2 text-sm text-ledger-sub hover:text-ledger-ink inline-flex items-center gap-1"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="calendar-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ledger-ink text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-60"
          >
            <CalendarPlus className="h-4 w-4" /> Save demo event
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
